<template>
  <div class="yt-widget">
    <iframe
      v-if="embedSrc"
      :key="embedSrc"
      class="yt-widget__frame"
      :src="embedSrc"
      title="YouTube Live"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
      allowfullscreen
      referrerpolicy="strict-origin-when-cross-origin"
    />
    <div v-else class="yt-widget__empty">
      <i class="fab fa-youtube yt-widget__icon"></i>
      <p class="mb-1">No stream configured</p>
      <p class="small text-muted mb-0">Open widget settings and paste a YouTube live URL or video ID.</p>
    </div>
  </div>
</template>

<script>
import serviceLinkMixin from '@/widgets/serviceLinkMixin'

export default {
  mixins: [serviceLinkMixin],
  props: {
    config: { type: Object, default: () => ({}) },
  },
  computed: {
    streamUrl() {
      if (this.usesService && this.activeService?.youtubeUrl) {
        return this.activeService.youtubeUrl
      }
      return this.config.url || this.config.videoId || ''
    },
    videoId() {
      return parseYouTubeVideoId(this.streamUrl)
    },
    channelId() {
      return parseYouTubeChannelId(this.streamUrl)
    },
    embedSrc() {
      const params = new URLSearchParams()
      if (this.config.autoplay !== false) params.set('autoplay', '1')
      if (this.config.muted !== false) params.set('mute', '1')
      params.set('rel', '0')
      params.set('modestbranding', '1')
      params.set('playsinline', '1')

      if (this.videoId) {
        return `https://www.youtube.com/embed/${this.videoId}?${params}`
      }
      if (this.channelId) {
        return `https://www.youtube.com/embed/live_stream?channel=${this.channelId}&${params}`
      }
      return ''
    },
  },
}

function parseYouTubeVideoId(input) {
  const s = String(input || '').trim()
  if (!s) return ''
  if (/^[a-zA-Z0-9_-]{11}$/.test(s)) return s

  try {
    const url = new URL(s.startsWith('http') ? s : `https://${s}`)
    const host = url.hostname.replace(/^www\./, '')
    if (host === 'youtu.be') {
      const id = url.pathname.split('/').filter(Boolean)[0]
      return /^[a-zA-Z0-9_-]{11}$/.test(id || '') ? id : ''
    }
    if (host.endsWith('youtube.com') || host.endsWith('youtube-nocookie.com')) {
      const v = url.searchParams.get('v')
      if (v && /^[a-zA-Z0-9_-]{11}$/.test(v)) return v
      const parts = url.pathname.split('/').filter(Boolean)
      const embedIdx = parts.indexOf('embed')
      if (embedIdx >= 0 && parts[embedIdx + 1] && parts[embedIdx + 1] !== 'live_stream') {
        const id = parts[embedIdx + 1]
        return /^[a-zA-Z0-9_-]{11}$/.test(id) ? id : ''
      }
      const liveIdx = parts.indexOf('live')
      if (liveIdx >= 0 && parts[liveIdx + 1] && /^[a-zA-Z0-9_-]{11}$/.test(parts[liveIdx + 1])) {
        return parts[liveIdx + 1]
      }
      const shortsIdx = parts.indexOf('shorts')
      if (shortsIdx >= 0 && parts[shortsIdx + 1] && /^[a-zA-Z0-9_-]{11}$/.test(parts[shortsIdx + 1])) {
        return parts[shortsIdx + 1]
      }
    }
  } catch (_) {
    /* ignore */
  }
  return ''
}

function parseYouTubeChannelId(input) {
  const s = String(input || '').trim()
  if (!s) return ''
  if (/^UC[\w-]{22}$/.test(s)) return s

  try {
    const url = new URL(s.startsWith('http') ? s : `https://${s}`)
    const host = url.hostname.replace(/^www\./, '')
    if (!host.endsWith('youtube.com')) return ''

    const channelParam = url.searchParams.get('channel')
    if (channelParam && /^UC[\w-]{22}$/.test(channelParam)) return channelParam

    const parts = url.pathname.split('/').filter(Boolean)
    const chIdx = parts.indexOf('channel')
    if (chIdx >= 0 && parts[chIdx + 1] && /^UC[\w-]{22}$/.test(parts[chIdx + 1])) {
      return parts[chIdx + 1]
    }
  } catch (_) {
    /* ignore */
  }
  return ''
}
</script>

<style scoped>
.yt-widget {
  position: relative;
  flex: 1;
  width: 100%;
  height: 100%;
  min-height: 0;
  background: #000;
  color: var(--ws-text);
}
.yt-widget__frame {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: 0;
  background: #000;
}
.yt-widget__empty {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 1rem;
  color: var(--ws-text-muted, #6c757d);
  background: var(--ws-widget-bg, #fff);
}
.yt-widget__icon {
  font-size: 2em;
  color: #ff0000;
  margin-bottom: 0.5rem;
}
</style>
